import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { AuthButton } from "./AuthButton";
import { useNavigate } from "react-router-dom";
import { AuthCard } from "./AuthCard";
import { SignUpFormProps, SignUpFormData } from "@/types";
import { Checkbox } from "../ui/checkbox";
import { TermsModal } from "../modals/TermsModal";
import { PrivacyPolicyModal } from "../modals/PrivacyPolicyModal"; // Import Privacy Policy Modal

const signUpSchema = z
  .object({
    firstName: z.string().min(2, "First name must be at least 2 characters"),
    lastName: z.string().min(2, "Last name must be at least 2 characters"),
    email: z.string().email("Please enter a valid email address"),
    password: z.string().min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string().min(6, "Password must be at least 6 characters"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

export const SignUpForm: React.FC<SignUpFormProps> = ({ onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false); // New state for Privacy Policy modal

  const form = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (data: SignUpFormData) => {
    if (!acceptedTerms) {
      toast({
        variant: "destructive",
        title: "Terms & Conditions",
        description: "You must accept the Terms & Conditions and Privacy Policy before signing up.",
      });
      return;
    }
  
    setIsLoading(true);
    try {
  
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            first_name: data.firstName,
            last_name: data.lastName,
          },
        },
      });
  
      if (signUpError) throw new Error(signUpError.message);
      if (!authData.user) throw new Error("No user data returned");
  
  
      toast({
        title: "Success",
        description: "Signed up and logged in successfully!",
      });
  
      onSuccess?.();
      navigate("/albums");
    } catch (error: any) {
      console.error("❌ Signup error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "An unexpected error occurred",
      });
    } finally {
      setIsLoading(false);
    }
  };
      
  return (
    <AuthCard title="">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2">
          <div className="grid grid-cols-2 gap-3">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm">First Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John" className="h-8" disabled={isLoading} {...field} />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm">Last Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Doe" className="h-8" disabled={isLoading} {...field} />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">Email</FormLabel>
                <FormControl>
                  <Input placeholder="Enter email" type="email" className="h-8" disabled={isLoading} {...field} />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
          <div className="grid grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">Password</FormLabel>
                <FormControl>
                  <Input placeholder="Create password" type="password" className="h-8" disabled={isLoading} {...field} />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">Confirm Password</FormLabel>
                <FormControl>
                  <Input placeholder="Confirm password" type="password" className="h-8" disabled={isLoading} {...field} />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
          </div>
          {/* Terms & Conditions Checkbox */}
              <div className="flex items-start space-x-2">
              <Checkbox
                id="terms"
                checked={acceptedTerms}
                onCheckedChange={(checked) => setAcceptedTerms(Boolean(checked))}
              />
              <div className="grid gap-1 leading-none">
                <label
                htmlFor="terms"
                className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                I agree to the{" "}
                <button type="button" className="text-primary hover:underline text-[11px]" onClick={() => setShowTerms(true)}>
                  Terms & Conditions
                </button>{" "}
                and{" "}
                <button type="button" className="text-primary hover:underline text-[11px]" onClick={() => setShowPrivacyPolicy(true)}>
                  Privacy Policy
                </button>
                </label>
              </div>
              </div>

          <AuthButton isLoading={isLoading} className="w-full h-8 mt-2" disabled={!acceptedTerms}>
            Create Account
          </AuthButton>
        </form>
      </Form>

      {/* Terms & Conditions Modal */}
      <TermsModal isOpen={showTerms} onClose={() => setShowTerms(false)} />
      
      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal isOpen={showPrivacyPolicy} onClose={() => setShowPrivacyPolicy(false)} />
    </AuthCard>
  );
};

export default SignUpForm;
